// Shared UI primitives — the PMS/LMS ui.js register adapted to --mms-*.
// Every surface uses these class strings so the design system stays one system.
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { t } from '../lib/i18n';

export const cardCls =
  'rounded-lg border border-mms-border bg-mms-surface shadow-[var(--mms-elev-1)] ' +
  'hover:border-[var(--mms-hover-border)] hover:shadow-[var(--mms-elev-2)]';
export const panelCls =
  'rounded-lg border border-mms-border bg-mms-surface shadow-[var(--mms-elev-1)]';

export const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-md bg-mms-accent px-3 py-1.5 text-sm ' +
  'font-semibold text-mms-accent-ink disabled:opacity-60 hover:brightness-110';
export const btnGhost =
  'inline-flex items-center gap-1.5 rounded-md border border-mms-border px-3 py-1.5 ' +
  'text-sm text-mms-muted hover:text-mms-text hover:border-[var(--mms-hover-border)]';
export const btnDanger =
  'inline-flex items-center gap-1.5 rounded-md border border-mms-border px-3 py-1.5 ' +
  'text-sm text-[var(--mms-danger)] hover:border-[var(--mms-danger)]';

export const inputCls =
  'w-full rounded-md border border-mms-border bg-mms-input px-3 py-2 text-sm ' +
  'outline-none focus:border-[var(--mms-focus-border)]';
export const labelCls = 'block text-sm text-mms-muted mb-1.5';

const STATUS_STYLE = {
  pending: 'text-[var(--mms-warning)] border-[var(--mms-warning)]',
  approved: 'text-[var(--mms-success)] border-[var(--mms-success)]',
  settled: 'text-[var(--mms-info)] border-[var(--mms-info)]',
  voided: 'text-mms-faint border-mms-border line-through',
  active: 'text-[var(--mms-success)] border-[var(--mms-success)]',
  inactive: 'text-mms-muted border-mms-border',
  awarded: 'text-[var(--mms-success)] border-[var(--mms-success)]',
  duplicate: 'text-mms-muted border-mms-border',
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${STATUS_STYLE[status] || 'text-mms-muted border-mms-border'}`}>
      {t(status || '—')}
    </span>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div>
      <span className={labelCls}>{label}</span>
      {children}
      {hint ? <p className="mt-1 text-xs text-mms-faint">{hint}</p> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide = false }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 p-4 pt-[8vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'} rounded-lg border border-mms-border bg-mms-surface shadow-[var(--mms-elev-3)]`}>
        <div className="flex items-center justify-between border-b border-mms-border px-5 py-3.5">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button onClick={onClose} aria-label={t('Close')} className="text-mms-muted hover:text-mms-text">
            <X size={16} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ children }) {
  return (
    <div className="rounded-lg border border-dashed border-mms-border px-6 py-10 text-center text-sm text-mms-muted">
      {children}
    </div>
  );
}
